<template>
    <OverlayBrowser :isOpen="openOverlay" :attachments="overlayAttachments" :startIndex="overlayStartIndex" @close="openOverlay = false" />
    <div class="col-12">
        <div v-if="email.type != 'y'" class="email-header">
            <span class="email-subject">{{ dynamicSubject || email.subject }}</span>
            <img :src="avatarSrc" class="avatar" width="72" />
            <div class="email-from">
                <p>
                    <span class="email-from-to-label">Van:</span>
                    <span class="email-from-name">{{ dynamicPersona ? dynamicPersona.displayName : email.sender }}</span> 
                    <br />
                    <span class="email-address">&lt;{{ dynamicPersona ? dynamicPersona.emailAddress : email.from }}&gt;</span>
                </p>
                <p>
                    <span class="email-from-to-label" v-if="email.receiver">Aan:</span>
                    <span class="email-to" v-if="email.receiver">
                        {{ email.receiver }}
                    </span>
                </p>
            </div>
        </div>

        <!-- Dynamic Message Content -->
        <div class="email-body" v-if="shouldUseDynamicContent">
            <!-- Loading state -->
            <div v-if="loadingMessage" class="text-center p-3">
                <div class="spinner-border" role="status">
                    <span class="visually-hidden">Laden...</span>
                </div>
                <p class="mt-2">Bericht wordt geladen...</p>
            </div>
            
            <!-- Error state -->
            <div v-else-if="messageLoadError" class="alert alert-warning">
                <strong>Waarschuwing:</strong> Kon dynamische content niet laden. Toon statische content.
                <br><small>Fout: {{ messageLoadError }}</small>
            </div>
            
            <!-- Dynamic content -->
            <div v-else>
                <!-- Email content -->
                <div v-if="dynamicMessage.type === 'email'">
                    <!-- Attachments -->
                    <div v-if="dynamicAttachments.length > 0" class="mb-3">
                        <span class="span-attachments icon-attachments">Bijlagen:</span>
                        <p class="attachments">
                            <a v-for="(attachment, index) in dynamicAttachments" 
                               :key="index"
                               class="btn-attachment"
                               @click.prevent="openOverlayWithAttachment(index)">
                               <span class="icon-attachments">
                                {{ attachment }}
                                </span>
                            </a>
                        </p>
                    </div>
                    
                    <!-- HTML Body -->
                    <div v-if="dynamicHtmlBody" v-html="dynamicHtmlBody"></div>
                    
                    <!-- Dynamic Signature -->
                    <div v-if="dynamicPersona && dynamicPersona.signatureHtml" 
                         v-html="dynamicPersona.signatureHtml"
                         class="dynamic-signature">
                    </div>
                    <div v-else-if="dynamicPersona" class="signature-fallback">
                        <p>Met vriendelijke groet,</p>
                        <p>{{ dynamicPersona.displayName }}</p>
                    </div>
                </div>
                
                <!-- Voicemail content -->
                <div v-else-if="dynamicMessage.type === 'voicemail'">
                    <div class="voicemail-container">
                        <h4>Voicemail</h4>
                        <p v-if="dynamicPersona">
                            <strong>Van:</strong> {{ dynamicPersona.displayName }}
                            <span v-if="dynamicPersona.emailAddress">({{ dynamicPersona.emailAddress }})</span>
                        </p>
                        
                        <!-- Audio player -->
                        <div v-if="dynamicAttachments.length > 0" class="voicemail-audio">
                            <audio controls :autoplay="autoPlayVoicemail" class="w-100">
                                <source :src="'/attachments/' + dynamicAttachments[0]" type="audio/mpeg">
                                <source :src="'/attachments/' + dynamicAttachments[0]" type="audio/wav">
                                Uw browser ondersteunt geen audio element.
                            </audio>
                        </div>
                        
                        <!-- Text content (transcript) -->
                        <div v-if="dynamicHtmlBody" class="voicemail-transcript mt-3">
                            <h5>Transcript:</h5>
                            <div v-html="dynamicHtmlBody"></div>
                        </div>
                    </div>
                </div>
                
                <!-- News content -->
                <div v-else-if="dynamicMessage.type === 'news'">
                    <div class="news-container">
                        <div class="news-header">
                            <h4>{{ dynamicSubject }}</h4>
                            <p v-if="dynamicPersona" class="news-source">
                                <strong>Bron:</strong> {{ dynamicPersona.displayName }}
                            </p>
                        </div>
                        <div v-if="dynamicHtmlBody" v-html="dynamicHtmlBody" class="news-content"></div>
                    </div>
                </div>
                
                <!-- LinkedIn content -->
                <div v-else-if="dynamicMessage.type === 'linkedin'">
                    <div class="linkedin-container">
                        <div class="linkedin-header">
                            <h4>{{ dynamicSubject }}</h4>
                            <p v-if="dynamicPersona" class="linkedin-source">
                                <strong>VerlinkedIn</strong>
                            </p>
                        </div>
                        <div v-if="dynamicHtmlBody" v-html="dynamicHtmlBody" class="linkedin-content"></div>
                    </div>
                </div>
                
                <!-- Radio content -->
                <div v-else-if="dynamicMessage.type === 'radio'">
                    <div class="radio-container">
                        <h4>Radio-uitzending</h4>
                        <p v-if="dynamicPersona">
                            <strong>Programma:</strong> {{ dynamicPersona.displayName }}
                        </p>
                        
                        <!-- Video/audio player -->
                        <div v-if="dynamicAttachments.length > 0" class="radio-audio">
                            <audio controls class="w-100" v-if="isAudioFile(dynamicAttachments[0])">
                                <source :src="'/attachments/' + dynamicAttachments[0]" type="audio/mpeg">
                                <source :src="'/attachments/' + dynamicAttachments[0]" type="audio/wav">
                                Uw browser ondersteunt geen audio element.
                            </audio>
                            <video controls class="w-100" v-else>
                                <source :src="'/attachments/' + dynamicAttachments[0]" type="video/mp4">
                                Uw browser ondersteunt geen video element.
                            </video>
                        </div>
                        
                        <div v-if="dynamicHtmlBody" v-html="dynamicHtmlBody" class="radio-transcript mt-3"></div>
                    </div>
                </div>
                
                <!-- Default content for unknown types -->
                <div v-else>
                    <div class="unknown-message-type">
                        <p><strong>Type:</strong> {{ dynamicMessage.type }}</p>
                        <div v-if="dynamicHtmlBody" v-html="dynamicHtmlBody"></div>
                    </div>
                </div>
                
                <!-- Special Effects Processing -->
                <div v-if="dynamicSpecialEffects" class="special-effects">
                    <!-- Process special effects to determine button functions -->
                    <div v-for="(effect, index) in (Array.isArray(dynamicSpecialEffects) ? dynamicSpecialEffects : [dynamicSpecialEffects])" 
                         :key="index">
                        <div v-if="effect.type === 'standard_answer' && effect.function">
                            <!-- This will trigger the standard answer function -->
                            <input type="hidden" :data-function="effect.function" />
                        </div>
                    </div>
                </div>
                
                <!-- Action buttons (based on step data from gametree) -->
                <div v-if="email.important">
                    <p class="p-important"><span class="span-important">Maak een keuze:</span></p>
                    <div class="action-container">
                        <TimeoutView v-if="email.duration && email.duration > 0 && !email.isDone" 
                                    :durationSeconds="email.duration / 1000"
                                    :remainingSeconds="email.remaining" />
                        
                        <!-- Get answer options from gametree -->
                        <div v-if="getCurrentStepAnswers().length > 0">
                            <ol class="action-options">
                                <li v-for="(answer, index) in getCurrentStepAnswers()" :key="index">
                                    <ButtonView :key="'btn-' + index"
                                       :done="email.isDone" 
                                       :tooLate="email.isTooLate" 
                                       :text="answer.button_title"
                                       class="small" 
                                       @click="handleDynamicAnswer(index)" />
                                    {{ answer.short_title }}
                                </li>
                            </ol>
                        </div>

                        <!-- Stamp overlay when done -->
                        <img v-if="email.isDone" 
                             src="@/assets/stamp_done.png" 
                             class="stamp-overlay" 
                             alt="Afgehandeld" />

                             <!-- Stamp overlay when done -->
                        <img v-if="!email.isDone && email.isTooLate" 
                             src="@/assets/stamp_too_late.png" 
                             class="stamp-overlay" 
                             alt="Te laat" />

                        <ButtonView 
                            v-if="email.isDone || email.isTooLate" 
                            class="admin-button small primary continue-button" 
                            @click="continueAfterAnswer" 
                            text="Doorgaan" />
                    </div>
                </div>

                <!-- Discussion step: timer, but no answers -->
                <div v-else-if="email.isDiscussion">
                    <!-- <p class="p-important"><span class="span-important">Bespreek met elkaar!</span></p> -->
                    <div class="action-container">
                        <TimeoutView v-if="!email.isDone"
                                    :friendlyMode="true"
                                    message="om te bespreken"
                                    :durationSeconds="email.duration / 1000"
                                    :remainingSeconds="email.remaining" />

                        <ButtonView 
                            v-if="!email.isDone" 
                            class="admin-button small primary continue-button" 
                            @click="continueAfterDiscussion" 
                            text="Doorgaan" />
                    </div>
                </div>
            </div>
        </div>

        <!-- Fallback message when no dynamic content is available -->
        <div class="email-body" v-else>
            <div class="alert alert-info">
                <h4>Geen scenario actief</h4>
                <p>Er is momenteel geen dynamisch scenario geladen. Neem contact op met de spelleider.</p>
            </div>
        </div>

        <div class="control-panel-content email-content container-fluid" style="margin-top: 1rem;" v-if="$route.params.reply">
            <router-view></router-view>
        </div>
    </div>
</template>

<script>
import api from '@/repository/api.js'
import ButtonView from '@/components/controlpanel/ButtonView.vue';
import OverlayBrowser from '@/components/OverlayBrowser.vue';
import TimeoutView from '@/components/controlpanel/TimeoutView.vue';

export default {
    name: "ControlPanelInboxEmail",
    components: {
        ButtonView, OverlayBrowser, TimeoutView
    },
    data() {
        return {
            radioSource: require("@/assets/sounds/radiouitzending.mp3"),
            email: {
                avatar: "ic_avatar.svg",
            },
            overlayAttachments: [],
            overlayStartIndex: 0,
            autoOpenPending: false,
            autoPlayVoicemail: false,
            openOverlay: false,
            gameInterval: null,
            dynamicMessage: null, // For dynamically loaded messages
            loadingMessage: false,
            messageLoadError: null,
        }
    },
    beforeRouteUpdate(to, from, next) {
        const messageId = to.params.messageId;
        this.refresh(messageId);
        next();
    },
    mounted() {
        const messageId = this.$route.params.messageId;
        this.refresh(messageId);
    },
    unmounted() {
        console.log('stopping the clock');
        this.stopClock();
    },
    computed: {
        autoOpenAttachments() {
            return process.env.VUE_APP_AUTO_OPEN_VIDEOS === 'true';
        },
        avatarSrc() {
            const filename = (this.dynamicPersona && this.dynamicPersona.avatarFilename) || this.email.avatar;
            try {
                return require('@/assets/' + filename);
            } catch (err) {
                // console.warn(`Avatar niet gevonden: ${filename}`);
                return require('@/assets/ic_avatar.svg');
            }
        },
        
        // Dynamic message computed properties
        hasDynamicMessage() {
            return this.dynamicMessage !== null;
        },
        
        dynamicSubject() {
            if (this.hasDynamicMessage && this.dynamicMessage.subject) {
                return this.dynamicMessage.subject;
            }
            return this.email.subject;
        },
        
        dynamicHtmlBody() {
            if (this.hasDynamicMessage && this.dynamicMessage.htmlBody) {
                return this.dynamicMessage.htmlBody;
            }
            return null;
        },
        
        dynamicPersona() {
            return this.hasDynamicMessage ? this.dynamicMessage.persona : null;
        },
        
        dynamicAttachments() {
            return this.hasDynamicMessage ? this.dynamicMessage.attachments : [];
        },
        
        dynamicSpecialEffects() {
            return this.hasDynamicMessage ? this.dynamicMessage.specialEffects : null;
        },
        
        shouldUseDynamicContent() {
            return this.hasDynamicMessage && !this.messageLoadError;
        },

        isLastStep() {
            const step = this.$store.state.session.steps.find(s => s.dilemmaId == this.email.dilemmaId);
            return !!(step && step.isEnd);
        }
    },
    methods: {
        async loadDynamicMessage(messageId) {
            if (this.loadingMessage) return;
            
            this.loadingMessage = true;
            this.messageLoadError = null;
            
            try {
                const token = this.$store.state.sessionToken;
                const response = await api.getMessage(token, messageId);
                this.dynamicMessage = response.data.data;
                console.log('Loaded dynamic message:', this.dynamicMessage);
                const autoOpen = this.autoOpenPending && this.dynamicAttachments.length > 0;
                this.autoOpenPending = false;
                if (autoOpen && this.dynamicMessage.type === 'voicemail') {
                    this.autoPlayVoicemail = true;
                } else if (autoOpen) {
                    this.openOverlayWithAttachment(0);
                }
            } catch (error) {
                console.error('Error loading dynamic message:', error);
                this.messageLoadError = error.message;
                this.dynamicMessage = null;
            } finally {
                this.loadingMessage = false;
            }
        },

        startClock() {
            if (this.gameInterval) {
                this.stopClock();
            }
            this.gameInterval = setInterval(this.tick, 1000);
        },
        stopClock() {
            if (this.gameInterval) {
                clearInterval(this.gameInterval);
            }
        },
        tick() {
            // Read the step from the store on every tick, so changed start times (pause, skipping steps) are picked up
            const session = this.$store.state.session;
            const step = session.steps ? session.steps.find(s => s.dilemmaId == this.email.dilemmaId) : null;
            if (!step) {
                return;
            }
            this.email.isDone = step.isDone;
            this.email.isTooLate = step.isTooLate;
            if (step.isDone || step.isTooLate || !step.startTime || !(step.duration > 0)) {
                this.email.remaining = 0;
                return;
            }
            const isFrozen = !!+session.isPaused && !session.endedAt && session.pausedAt;
            const now = isFrozen ? Date.parse(session.pausedAt) : Date.now();
            const timerEnd = Date.parse(step.startTime) + step.duration;
            this.email.remaining = Math.max(Math.floor((timerEnd - now) / 1000), 0);
            if (this.email.remaining <= 0) {
                this.email.isTooLate = true;
            }
        },
        refresh(messageId) {
            //console.log(`Reading email with id: ${messageId}`);
            // Try to load dynamic message content
            this.loadDynamicMessage(messageId);

            // Find the live step data (dynamic scenario) matching this message
            const dilemma = this.$store.state.session.steps.find((s) => s.dilemmaId == messageId);
            if (!dilemma) {
                console.error(`no dilemma for message: ${messageId}`);
                return;
            }
            this.autoOpenPending = this.autoOpenAttachments && !dilemma.isRead;

            // Mark email as read when opened
            if (!dilemma.isRead) {
                const token = this.$store.state.sessionToken;
                api.emailOpened(token, messageId)
                    .then(() => {
                        console.log(`Marked email ${messageId} as read`);
                    })
                    .catch((error) => {
                        console.error('Error marking email as read:', error);
                    });
            }

            const important = !!(dilemma.answers && dilemma.answers.length > 0);
            const e = {
                id: messageId,
                dilemmaId: dilemma.dilemmaId,
                important: important,
                // A step with a timer but without answers invites the players to discuss the message
                isDiscussion: !important && dilemma.duration > 0,
                duration: dilemma.duration,
            };

            e.isDone = dilemma.isDone;
            e.isRead = dilemma.isRead;
            e.isTooLate = dilemma.isTooLate;
            e.remaining = 0;

            this.email = { ...this.email, ...e };

            // Check if the email has a timeout
            this.stopClock();
            if ((e.important || e.isDiscussion) && !dilemma.isDone && dilemma.duration > 0) {
                this.tick();
                this.startClock();
            }
        },

        getCurrentStepAnswers() {
            try {
                if (!this.email || !this.email.dilemmaId) return [];

                const step = this.$store.state.session.steps.find(s => s.dilemmaId === this.email.dilemmaId);
                let arr = step ? (step.answers || []) : [];
                
                // #HACKY WARNING
                // In the sheet, there might be newlines.
                // These are used to separate the short name (for in the button)
                // and the long name (for in the numbered list above the buttons)
                
                return arr.map(a => {
                    // Check if the answer.title contains a newline,
                    // then split the first and second ones.
                    if (a['title'].indexOf('\n') > -1) {
                        let chunks = a['title'].split('\n');
                        a['button_title'] = chunks[0];
                        a['short_title'] = chunks[1];
                    } else {
                        a['button_title'] = a['title'];
                        a['short_title'] = a['title'];
                    }
                    return a;
                });
            } catch(err) {
                // Likely that the user has just logged out
                console.error(err);
                return [];
            }
        },

        handleDynamicAnswer(answerIndex) {
            if (window.confirm("Weet je het zeker?")) {
                this.answerEmail(answerIndex);
            }
        },

        isAudioFile(filename) {
            if (!filename) return false;
            const audioExtensions = ['.mp3', '.wav', '.ogg', '.m4a', '.aac'];
            const extension = filename.toLowerCase().substring(filename.lastIndexOf('.'));
            return audioExtensions.includes(extension);
        },

        openOverlayWithAttachment(index) {
            this.overlayAttachments = this.dynamicAttachments.map(a => '/attachments/' + a);
            this.overlayStartIndex = index;
            this.openOverlay = true;
        },

        // Generic answer function (simplified from original)
        async answerEmail(answerIndex) {
            try {
                const token = this.$store.state.sessionToken;
                const progress = this.$store.state.session.steps.findIndex(s => s.dilemmaId === this.email.dilemmaId);

                await api.answerDilemma(token, this.email.dilemmaId, progress, answerIndex);

                // Mark as done in the store, so the tick and polling don't reset it to the stale value
                this.$store.dispatch('markAsDone', this.email.dilemmaId);
                this.email.isDone = true;
                
            } catch (error) {
                console.error('Error answering dilemma:', error);
            }
        },

        // A discussion step has no answers; the dummy answer only marks the step as done, so the backend can progress
        async continueAfterDiscussion() {
            await this.answerEmail(-1);
            this.continueAfterAnswer();
        },

        continueAfterAnswer() {
            if (this.isLastStep) {
                this.$router.push('/control-panel/dashboard');
            } else {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        }
    }
}
</script>

<style scoped>
.email-subject {
    font-weight: bold;
    font-size: 1.2em;
    display: block;
    margin-bottom: 1rem;
}

.avatar {
    float: left;
    margin-right: 1rem;
    border-radius: 50%;
}

.email-from {
    font-size: 0.9em;
    color: #666;
    margin-bottom: 1rem;
}

.email-to {
    font-size: 0.8em;
    color: #888;
}

.email-body {
    margin-bottom: 1rem;
    /* background: white;
    padding: 1.5rem;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1); */
}

.span-attachments {
    font-weight: bold;
    color: #0066cc;
}

.attachments {
    margin: 0.5rem 0;
}

.btn-attachment {
    display: inline-block;
    padding: 0.5rem 1rem;
    margin-right: 0.5rem;
    background: #f0f0f0;
    border: 2px solid #9e8d8d;
    box-shadow: 2px 3px 0px rgba(0,0,0,0.5);
    border-radius: 4px;
    text-decoration: none;
    color: #333;
    cursor: pointer;
}

.btn-attachment:hover {
    background: #e0e0e0;
}
.btn-attachment:active {
    box-shadow: none;
    transform: translate(2px, 3px);
}

.p-important {
    margin-top: 2em;
}
.span-important {
    font-weight: bold;
    color: #d63384;
}

.action-container {
    position: relative;
}

.action-options {
    margin: 1rem 0;
    padding-left: 1.5rem;
}
.action-options .admin-button {
    margin-bottom: 0.5rem;
}
.action-options li {
    margin-bottom: 2.5rem;
}

.stamp-overlay {
    position: absolute;
    top: 67%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 420px;
    height: auto;
    pointer-events: none;
    z-index: 10;
}

.continue-button {
    /* position: absolute;
    right: 0;
    margin: 0; */
    bottom: -1em;
    /* z-index: 20; */
}

/* Message type specific styles */
.voicemail-container {
    border-left: 4px solid #0066cc;
    padding-left: 1rem;
}

.news-container {
    border-left: 4px solid #ff6600;
    padding-left: 1rem;
}

.linkedin-container {
    border-left: 4px solid #0077b5;
    padding-left: 1rem;
}

.radio-container {
    border-left: 4px solid #9900cc;
    padding-left: 1rem;
}

.news-source, .linkedin-source {
    font-size: 0.9em;
    color: #666;
    font-style: italic;
}

.voicemail-audio, .radio-audio {
    margin: 1rem 0;
}

.voicemail-transcript, .radio-transcript {
    border-top: 1px solid #eee;
    padding-top: 1rem;
}

.dynamic-signature {
    margin-top: 2rem;
    padding-top: 1rem;
    border-top: 1px solid #eee;
}

.signature-fallback {
    margin-top: 2rem;
    padding-top: 1rem;
    border-top: 1px solid #eee;
    font-style: italic;
}

.unknown-message-type {
    padding: 1rem;
    background: #fff3cd;
    border: 1px solid #ffeaa7;
    border-radius: 4px;
}

.email-subject::before {
    content: "Onderwerp: ";
}
.email-from-name {
    font-weight: bold;
    font-size: 90%;
}
.email-address {
    font-weight: normal;
    font-size: 90%;
    font-style: italic;
    display: inline-block;
}
.email-from-to-label {
    font-size: 80%;
    margin-right: 0.5rem;
}
.email-header {
    margin-bottom: 1rem;
    border-bottom: 1px solid #eee;
}
</style>